'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, RefreshCw, AlertTriangle, Activity, BarChart2, Zap } from 'lucide-react';

interface Props {
  underlying: string;
  spotPrice?: number;
}

interface Suggestion {
  type: 'BUY' | 'SELL';
  optionType: 'CE' | 'PE';
  strike: number;
  entryPrice: number;
  stopLoss: number;
  exitTarget: number;
  confidence: number; // 0-100
  rationale: string[];
}

interface TechnicalSignals {
  rsi: number;
  macd: 'bullish' | 'bearish' | 'neutral';
  trend: 'uptrend' | 'downtrend' | 'sideways';
  pcr: number;
  ivRank: number;
  oiBuildup: 'call_writing' | 'put_writing' | 'neutral';
  sentiment: 'bullish' | 'bearish' | 'neutral';
  vix: number;
}

function computeSignals(underlying: string, spotPrice: number): TechnicalSignals {
  // Deterministic pseudo-signals based on underlying + spot (no random, refreshes on spot change)
  const seed = (spotPrice % 100) / 100;
  const rsiBase: Record<string, number> = {
    NIFTY: 54, BANKNIFTY: 48, FINNIFTY: 61, SENSEX: 52, MIDCPNIFTY: 57, MIDCAP150: 59,
  };
  const rsi = (rsiBase[underlying] ?? 54) + Math.round(seed * 12 - 6);
  const macd: TechnicalSignals['macd'] = rsi > 55 ? 'bullish' : rsi < 45 ? 'bearish' : 'neutral';
  const trend: TechnicalSignals['trend'] = rsi > 58 ? 'uptrend' : rsi < 42 ? 'downtrend' : 'sideways';
  const pcrBase: Record<string, number> = {
    NIFTY: 1.24, BANKNIFTY: 0.87, FINNIFTY: 1.05, SENSEX: 1.18, MIDCPNIFTY: 1.12, MIDCAP150: 1.08,
  };
  const pcr = pcrBase[underlying] ?? 1.0;
  const ivRankBase: Record<string, number> = {
    NIFTY: 42, BANKNIFTY: 58, FINNIFTY: 47, SENSEX: 39, MIDCPNIFTY: 44, MIDCAP150: 41,
  };
  const ivRank = ivRankBase[underlying] ?? 45;
  const oiBuildup: TechnicalSignals['oiBuildup'] = pcr > 1.1 ? 'put_writing' : pcr < 0.9 ? 'call_writing' : 'neutral';
  const sentiment: TechnicalSignals['sentiment'] = (macd === 'bullish' && pcr > 1) ? 'bullish' : (macd === 'bearish' && pcr < 1) ? 'bearish' : 'neutral';
  const vixBase: Record<string, number> = {
    NIFTY: 14.2, BANKNIFTY: 18.6, FINNIFTY: 16.4, SENSEX: 13.8, MIDCPNIFTY: 17.2, MIDCAP150: 15.9,
  };
  const vix = vixBase[underlying] ?? 15;
  return { rsi, macd, trend, pcr, ivRank, oiBuildup, sentiment, vix };
}

function getATMStrike(spotPrice: number, underlying: string): number {
  const stepMap: Record<string, number> = {
    NIFTY: 50, BANKNIFTY: 100, FINNIFTY: 50, SENSEX: 100, MIDCPNIFTY: 25, MIDCAP150: 50,
  };
  const step = stepMap[underlying] ?? 50;
  return Math.round(spotPrice / step) * step;
}

function buildSuggestions(underlying: string, spotPrice: number, signals: TechnicalSignals): Suggestion[] {
  const atm = getATMStrike(spotPrice, underlying);
  const stepMap: Record<string, number> = {
    NIFTY: 50, BANKNIFTY: 100, FINNIFTY: 50, SENSEX: 100, MIDCPNIFTY: 25, MIDCAP150: 50,
  };
  const step = stepMap[underlying] ?? 50;

  const suggestions: Suggestion[] = [];

  // ---- CE BUY suggestion ----
  if (signals.sentiment === 'bullish' || signals.macd === 'bullish') {
    const ceStrike = atm + step; // slightly OTM call
    const ceEntry = Math.round(spotPrice * 0.008 * 10) / 10; // ~0.8% of spot
    suggestions.push({
      type: 'BUY',
      optionType: 'CE',
      strike: ceStrike,
      entryPrice: ceEntry,
      stopLoss: Math.round(ceEntry * 0.65 * 10) / 10,
      exitTarget: Math.round(ceEntry * 1.8 * 10) / 10,
      confidence: Math.min(90, 55 + Math.round((signals.rsi - 50) * 1.5) + (signals.pcr > 1.1 ? 10 : 0)),
      rationale: [
        `RSI at ${signals.rsi} — ${signals.rsi > 55 ? 'momentum building' : 'neutral zone'}`,
        `MACD ${signals.macd} crossover detected`,
        `PCR ${signals.pcr.toFixed(2)} — ${signals.pcr > 1 ? 'put writers active (bullish)' : 'call writers active'}`,
        `OI buildup: ${signals.oiBuildup === 'put_writing' ? 'Heavy put writing at lower strikes' : 'Mixed OI signals'}`,
        `VIX at ${signals.vix} — ${signals.vix < 16 ? 'low volatility, options cheap' : 'elevated IV, use tight SL'}`,
      ],
    });
  }

  // ---- CE SELL suggestion ----
  if (signals.sentiment === 'bearish' || signals.oiBuildup === 'call_writing') {
    const ceStrike = atm + step * 2;
    const ceEntry = Math.round(spotPrice * 0.005 * 10) / 10;
    suggestions.push({
      type: 'SELL',
      optionType: 'CE',
      strike: ceStrike,
      entryPrice: ceEntry,
      stopLoss: Math.round(ceEntry * 1.5 * 10) / 10,
      exitTarget: Math.round(ceEntry * 0.3 * 10) / 10,
      confidence: Math.min(88, 50 + Math.round((55 - signals.rsi) * 1.2) + (signals.ivRank > 50 ? 12 : 0)),
      rationale: [
        `RSI at ${signals.rsi} — ${signals.rsi < 50 ? 'bearish momentum' : 'overbought risk'}`,
        `Call OI buildup at ${ceStrike} — resistance zone`,
        `IV Rank ${signals.ivRank}% — ${signals.ivRank > 50 ? 'high IV, sell premium' : 'moderate IV'}`,
        `MACD ${signals.macd} — trend confirmation`,
        `Market sentiment: ${signals.sentiment}`,
      ],
    });
  }

  // ---- PE BUY suggestion ----
  if (signals.sentiment === 'bearish' || signals.macd === 'bearish') {
    const peStrike = atm - step;
    const peEntry = Math.round(spotPrice * 0.007 * 10) / 10;
    suggestions.push({
      type: 'BUY',
      optionType: 'PE',
      strike: peStrike,
      entryPrice: peEntry,
      stopLoss: Math.round(peEntry * 0.6 * 10) / 10,
      exitTarget: Math.round(peEntry * 1.9 * 10) / 10,
      confidence: Math.min(87, 52 + Math.round((50 - signals.rsi) * 1.4) + (signals.pcr < 0.9 ? 10 : 0)),
      rationale: [
        `RSI at ${signals.rsi} — ${signals.rsi < 45 ? 'bearish momentum confirmed' : 'approaching oversold'}`,
        `MACD ${signals.macd} — downside momentum`,
        `PCR ${signals.pcr.toFixed(2)} — ${signals.pcr < 1 ? 'call writers dominating (bearish)' : 'mixed signals'}`,
        `Put OI buildup at ${peStrike} — support breakdown risk`,
        `VIX ${signals.vix} — volatility ${signals.vix > 16 ? 'elevated, good for puts' : 'low, hedge cheaply'}`,
      ],
    });
  }

  // ---- PE SELL suggestion ----
  if (signals.sentiment === 'bullish' || signals.oiBuildup === 'put_writing') {
    const peStrike = atm - step * 2;
    const peEntry = Math.round(spotPrice * 0.004 * 10) / 10;
    suggestions.push({
      type: 'SELL',
      optionType: 'PE',
      strike: peStrike,
      entryPrice: peEntry,
      stopLoss: Math.round(peEntry * 1.6 * 10) / 10,
      exitTarget: Math.round(peEntry * 0.25 * 10) / 10,
      confidence: Math.min(85, 48 + Math.round((signals.rsi - 50) * 1.1) + (signals.ivRank > 45 ? 10 : 0)),
      rationale: [
        `RSI at ${signals.rsi} — ${signals.rsi > 50 ? 'bullish bias' : 'neutral'}`,
        `Heavy put writing at ${peStrike} — strong support`,
        `IV Rank ${signals.ivRank}% — ${signals.ivRank > 45 ? 'sell premium opportunity' : 'moderate premium'}`,
        `PCR ${signals.pcr.toFixed(2)} — put writers active`,
        `Trend: ${signals.trend} — ${signals.trend === 'uptrend' ? 'puts likely to decay' : 'sideways decay expected'}`,
      ],
    });
  }

  // Fallback: always provide at least 2 suggestions
  if (suggestions.length === 0) {
    const ceEntry = Math.round(spotPrice * 0.006 * 10) / 10;
    const peEntry = Math.round(spotPrice * 0.006 * 10) / 10;
    suggestions.push(
      {
        type: 'BUY', optionType: 'CE', strike: atm + step,
        entryPrice: ceEntry, stopLoss: Math.round(ceEntry * 0.65 * 10) / 10, exitTarget: Math.round(ceEntry * 1.7 * 10) / 10,
        confidence: 58,
        rationale: ['Neutral market — ATM+1 CE for directional play', `RSI ${signals.rsi} — sideways momentum`, 'Low conviction, use small position size'],
      },
      {
        type: 'BUY', optionType: 'PE', strike: atm - step,
        entryPrice: peEntry, stopLoss: Math.round(peEntry * 0.65 * 10) / 10, exitTarget: Math.round(peEntry * 1.7 * 10) / 10,
        confidence: 55,
        rationale: ['Hedge position — ATM-1 PE', `PCR ${signals.pcr.toFixed(2)} — balanced market`, 'Sideways — straddle or hedge recommended'],
      }
    );
  }

  return suggestions;
}

const SIGNAL_LABELS: Record<string, string> = {
  bullish: '🐂 Bullish', bearish: '🐻 Bearish', neutral: '⚖️ Neutral',
  uptrend: '↑ Uptrend', downtrend: '↓ Downtrend', sideways: '↔ Sideways',
  put_writing: 'Put Writing', call_writing: 'Call Writing',
};

export default function LivePredictionSection({ underlying, spotPrice }: Props) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [signals, setSignals] = useState<TechnicalSignals | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(() => {
    if (!spotPrice) return;
    setLoading(true);
    setTimeout(() => {
      const s = computeSignals(underlying, spotPrice);
      setSignals(s);
      setSuggestions(buildSuggestions(underlying, spotPrice, s));
      setLastUpdated(new Date());
      setLoading(false);
    }, 600);
  }, [underlying, spotPrice]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const confidenceColor = (c: number) =>
    c >= 75 ? 'text-positive' : c >= 60 ? 'text-amber-400' : 'text-muted-foreground';

  const confidenceBg = (c: number) =>
    c >= 75 ? 'bg-positive' : c >= 60 ? 'bg-amber-400' : 'bg-muted-foreground';

  return (
    <div className="card-elevated p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
            <Zap className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Live AI Prediction — {underlying}</h3>
            <p className="text-2xs text-muted-foreground">Technical analysis · OI · Sentiment · MACD · RSI</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-2xs text-muted-foreground">
            Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
          <button
            onClick={refresh}
            disabled={loading || !spotPrice}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-4">
        <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
        <p className="text-2xs text-amber-300/80">
          AI predictions are for educational purposes only. Not SEBI-registered advice. Always do your own research before trading.
        </p>
      </div>

      {/* Technical Signals Bar */}
      {signals && (
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 mb-4 p-3 rounded-xl bg-muted/30 border border-border">
          {[
            { icon: <Activity className="w-3 h-3" />, label: 'RSI', val: signals.rsi.toString(), color: signals.rsi > 60 ? 'text-positive' : signals.rsi < 40 ? 'text-negative' : 'text-amber-400' },
            { icon: <BarChart2 className="w-3 h-3" />, label: 'MACD', val: SIGNAL_LABELS[signals.macd] ?? signals.macd, color: signals.macd === 'bullish' ? 'text-positive' : signals.macd === 'bearish' ? 'text-negative' : 'text-amber-400' },
            { icon: <TrendingUp className="w-3 h-3" />, label: 'Trend', val: SIGNAL_LABELS[signals.trend] ?? signals.trend, color: signals.trend === 'uptrend' ? 'text-positive' : signals.trend === 'downtrend' ? 'text-negative' : 'text-amber-400' },
            { icon: <BarChart2 className="w-3 h-3" />, label: 'PCR', val: signals.pcr.toFixed(2), color: signals.pcr > 1 ? 'text-positive' : 'text-negative' },
            { icon: <Activity className="w-3 h-3" />, label: 'IV Rank', val: `${signals.ivRank}%`, color: signals.ivRank > 60 ? 'text-negative' : 'text-amber-400' },
            { icon: <BarChart2 className="w-3 h-3" />, label: 'OI Flow', val: SIGNAL_LABELS[signals.oiBuildup] ?? signals.oiBuildup, color: signals.oiBuildup === 'put_writing' ? 'text-positive' : signals.oiBuildup === 'call_writing' ? 'text-negative' : 'text-muted-foreground' },
            { icon: <Zap className="w-3 h-3" />, label: 'Sentiment', val: SIGNAL_LABELS[signals.sentiment] ?? signals.sentiment, color: signals.sentiment === 'bullish' ? 'text-positive' : signals.sentiment === 'bearish' ? 'text-negative' : 'text-amber-400' },
          ].map((sig) => (
            <div key={sig.label} className="flex flex-col items-center gap-0.5 text-center">
              <span className="text-muted-foreground">{sig.icon}</span>
              <span className="text-2xs text-muted-foreground">{sig.label}</span>
              <span className={`text-2xs font-bold ${sig.color}`}>{sig.val}</span>
            </div>
          ))}
        </div>
      )}

      {/* Suggestion Cards */}
      {!spotPrice ? (
        <div className="text-center py-8 text-muted-foreground text-sm">Waiting for live spot price…</div>
      ) : loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-40 rounded-xl bg-muted/30 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {suggestions.map((s, idx) => (
            <div
              key={`sug-${idx}`}
              className={`rounded-xl border p-3.5 flex flex-col gap-2.5 ${
                s.type === 'BUY' ?'bg-positive-subtle border-positive/20' :'bg-negative-subtle border-negative/20'
              }`}
            >
              {/* Card Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  {s.type === 'BUY' ? (
                    <TrendingUp className="w-4 h-4 text-positive" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-negative" />
                  )}
                  <span className={`text-xs font-bold ${s.type === 'BUY' ? 'text-positive' : 'text-negative'}`}>
                    {s.type} {s.optionType}
                  </span>
                </div>
                <span className="text-xs font-bold text-foreground font-tabular">
                  ₹{s.strike.toLocaleString('en-IN')}
                </span>
              </div>

              {/* Confidence Bar */}
              <div>
                <div className="flex justify-between text-2xs mb-1">
                  <span className="text-muted-foreground">Confidence</span>
                  <span className={`font-bold font-tabular ${confidenceColor(s.confidence)}`}>{s.confidence}%</span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${confidenceBg(s.confidence)}`}
                    style={{ width: `${s.confidence}%` }}
                  />
                </div>
              </div>

              {/* Price Levels */}
              <div className="grid grid-cols-3 gap-1 text-center">
                <div className="rounded-lg bg-card/60 p-1.5">
                  <p className="text-2xs text-muted-foreground mb-0.5">Entry</p>
                  <p className="text-xs font-bold font-tabular text-foreground">₹{s.entryPrice}</p>
                </div>
                <div className="rounded-lg bg-negative/10 p-1.5">
                  <p className="text-2xs text-muted-foreground mb-0.5">Stop Loss</p>
                  <p className="text-xs font-bold font-tabular text-negative">₹{s.stopLoss}</p>
                </div>
                <div className="rounded-lg bg-positive/10 p-1.5">
                  <p className="text-2xs text-muted-foreground mb-0.5">Target</p>
                  <p className="text-xs font-bold font-tabular text-positive">₹{s.exitTarget}</p>
                </div>
              </div>

              {/* Rationale */}
              <div className="space-y-0.5">
                {s.rationale.slice(0, 3).map((r, ri) => (
                  <p key={ri} className="text-2xs text-muted-foreground flex items-start gap-1">
                    <span className="mt-0.5 shrink-0 w-1 h-1 rounded-full bg-muted-foreground inline-block" />
                    {r}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
